import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import AlertDialog from '../../../components/ui/Dialog/AlertDialog';
import Preloader from '../../../components/ui/Preloader/Preloader';

import { IAppState } from '../../../store/rootDuck';
import { actions as categoriesActions } from '../../../store/ducks/categories.duck';
import { actions as productTypesActions } from '../../../store/ducks/productType.duck';
import { actions as productCatalogActions } from '../../../store/ducks/product-catalog.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import { actions as cartActions } from '../../../store/ducks/cart.duck';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import useCrudSnackbar from '../../../hooks/useCrudSnackbar';
import { IProduct } from '../../../interfaces/product';
import { ProductsGrid } from './components';
import ProductsTableListComponent from './components/ProductTable/ProductsTableList';
import FilterCategoriesTree from './components/FilterCategoriesTree';
import { useStylesProductCatalog } from './hooks/useStyles';
import { useFormatMessage } from '../../../hooks';
import { ILocation } from '../../../interfaces/locations';
import ProductsTableListForAdminComponent from './components/ProductsTableListForAdmin';
import ProductsTableForUsersComponent from './components/ProductsTableForUsers';
import ProductCatalogHeader from './components/ProductCatalogHeader/ProductCatalogHeader';
import { useCatalogSearch } from './hooks/useCatalogSearch';

type CategoriesMethods = {
  resetCategories: () => void;
} | null;

const ProductCatalog: React.FC<TPropsFromRedux> = ({
  search,
  sortType,
  products,
  loading,
  page,
  perPage,
  total,
  deleteError,
  me,
  loadingMe,
  addProductLoading,
  addProductSuccess,
  addProductError,
  cartCompanyId,
  isAuthorized,
  companyGuestCart,
  catalogCategories,
  isAdmin,
  fetch,
  clearProducts,
  delProduct,
  clearDel,
  fetchMe,
  clearMe,
  addProductToCart,
  setProductGuestCart,
  fetchCatalogCategories,
  viewType,
  setViewType,
  setSearch,
  isSearching,
  typeParams,
  fetchTypeById,
  clearParams,
  fetchType,
  clearAddProduct,
  delSuccess,
  loadingTypes,
  categoryIdActive,
  setCategoryIdActive,
  setSearchText,
}) => {
  const { category } = useCatalogSearch();
  const { classes } = useStylesProductCatalog();
  const categoriesRef = useRef<CategoriesMethods>(null);
  const intl = useIntl();
  const fm = useFormatMessage();
  const navigate = useNavigate();
  const locationHistory: any = useLocation();
  const { state: locationState }: any = locationHistory;
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [deleteId, setDeleteId] = useState<number | undefined>(-1);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(0);
  const [filterData, setFilterData] = useState<any>({});
  const [priceFrom, setPriceFrom] = useState<string | number>('');
  const [priceTo, setPriceTo] = useState<string | number>('');
  const [selectedOption, setSelectedOption] = useState(
    (sortType && sortType) || 'salesVolume'
  );
  const categoryId = category || categoryIdActive || selectedCategoryId || 0;

  // Скрываем табличный вид на мобильной версии
  const effectiveViewType = isMobile && viewType === 'list' ? 'grid' : viewType;

  const clearAllFilters = useCallback(() => {
    localStorage.removeItem('category');
    localStorage.removeItem('filter');
    localStorage.removeItem('selectedTypeId');
    localStorage.removeItem('priceTo');
    localStorage.removeItem('priceFrom');
    // clearAllParam();
    clearParams();
  }, []);

  const locale = useMemo<ILocation | undefined | null>(() => {
    const localLocation = localStorage.getItem('location');

    if (localLocation) {
      try {
        const parseLocalLocation = JSON.parse(localLocation);
        return parseLocalLocation as ILocation;
      } catch (error) {
        console.error('Ошибка при парсинге JSON:', error);
      }
    }

    return undefined;
  }, []);

  const filter = useMemo(() => {
    const data = [];
    // eslint-disable-next-line guard-for-in
    for (const key in filterData) {
      data.push({
        parameter: Number(key),
        values: filterData[key].values?.map((el: any) => (el === '' ? 0 : el)),
      });
    }
    if (data.length) {
      return { parameters: data, price_from: priceFrom, price_to: priceTo };
    }
    return { parameters: [], price_from: priceFrom, price_to: priceTo };
  }, [filterData, priceFrom, priceTo]);

  const handleDeleteDialog = useCallback((item: IProduct) => {
    setDeleteId(item.id);
    setAlertOpen(true);
    // }
  }, []);

  useEffect(() => {
    fetchMe();
    clearParams();
    fetchType();
    return () => {
      clearAllFilters();
      clearMe();
      clearProducts();
      clearDel();
      clearAddProduct();
      clearParams();
    };
  }, []);

  useEffect(() => {
    fetchCatalogCategories({ with_products: true, only_active: 1 });
  }, [fetchCatalogCategories]);

  useEffect(() => {}, [typeParams]);

  const deleteAction = useCallback(() => {
    setAlertOpen(false);
    delProduct({
      id: deleteId,
      page,
      perPage,
      filter,
      categoryId,
    });
  }, [deleteId, delProduct, perPage, page, filter, categoryId]);

  const editAction = useCallback(
    (item: IProduct, isReviews?: boolean) => {
      setSearchText('');
      navigate(`/products/edit/${item.id}`, { state: { isReviews } });
    },
    [navigate]
  );

  const handleCartDialog = useCallback(
    (item: any, count = 1) => {
      if (!isAuthorized) {
        setProductGuestCart({ data: item, type: 'cart', count });
      }
    },
    [addProductToCart, cartCompanyId, companyGuestCart, setProductGuestCart]
  );

  useCrudSnackbar({
    success: addProductSuccess,
    error: addProductError,
    successMessage: intl.formatMessage({ id: 'CART.ADD_PRODUCT.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${addProductError}`,
    afterSuccessOrError: () => {},
  });

  useCrudSnackbar({
    success: delSuccess,
    error: deleteError,
    successMessage: intl.formatMessage({ id: 'PRODUCT.DELETED' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${addProductError}`,
    afterSuccessOrError: () => {
      setAlertOpen(false);
      clearDel();
      fetch({
        page,
        perPage,
        filter,
        categoryId,
        sort_type: locale ? 'distantion' : selectedOption,
        locale,
        search,
      });
    },
  });
  useEffect(() => {
    const sessionViewType = sessionStorage.getItem('viewType');
    setViewType((sessionViewType as 'table' | 'grid' | 'list') || 'grid');
  }, []);

  useEffect(() => {
    if (products.length && !loading && !loadingMe && !loadingTypes) {
      setTimeout(() => {
        const savedScroll = sessionStorage.getItem('productCatalogScrollPosition');
        if (savedScroll) {
          const parsedScroll = JSON.parse(savedScroll);
          if (parsedScroll?.categoryId === categoryIdActive) {
            window.scrollTo({ top: parseInt(parsedScroll.value, 10), behavior: 'auto' });
          }
        }
      }, 300);
    }

    const handleScroll = () => {
      if (products.length && !loading && !loadingMe && !loadingTypes) {
        sessionStorage.setItem(
          'productCatalogScrollPosition',
          JSON.stringify({
            value: window.scrollY.toString(),
            categoryId: categoryIdActive,
          })
        );
      }
    };
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [products, loading, loadingMe, loadingTypes]);

  useEffect(() => {
    if (locationHistory?.state?.categoryId) {
      setSearch(true);
      // setParams('category', locationHistory.state.categoryId);
      setSelectedCategoryId(locationHistory.state.categoryId);
      setCategoryIdActive(locationHistory.state.categoryId);
      // fetch({ page, perPage, filter, categoryId: location.state.categoryId, });
      // setParamsSearch();
      // history.replace({ ...location, state: undefined });
    } else {
      // setParamsSearch();
    }
    search && setSearch(true);
    // fetchType();
  }, [sortType, search]);

  useEffect(() => {
    if (deleteError) {
      enqueueSnackbar(deleteError, { variant: 'error' });
      setAlertOpen(false);
      clearDel();
    }
    return () => {
      clearDel();
    };
  }, [deleteError, clearDel, enqueueSnackbar]);

  useEffect(() => {
    locationState?.filter?.price_from && setPriceFrom(locationState.filter.price_from);
    locationState?.filter?.price_to && setPriceTo(locationState.filter.price_to);
    if (locationState?.productTypeId) {
      fetchTypeById({ id: locationState.productTypeId, filters: true });
    }
    if (locationState?.filter?.parameters) {
      setFilterData(locationState.filter.parameters);
      fetch({
        // page,
        // perPage,
        filter: {
          parameters: locationState?.paramsArray || [],
          price_from: locationState?.filter?.price_from || '',
          price_to: locationState?.filter?.price_to || '',
        },
        categoryId,
        sort_type: locale ? 'distantion' : selectedOption,
        locale,
        search,
      });
      setSearch(true);
    } else {
      fetch({
        // page,
        // perPage,
        filter,
        categoryId,
        sort_type: locale ? 'distantion' : selectedOption,
        locale,
        search,
      });
    }
  }, [locationState]);

  useEffect(() => {
    localStorage.setItem('filter', JSON.stringify(filterData));
  }, [filterData]);

  const findPathById = (arr: any[], targetId: number | undefined | null, path = []): any => {
    if (!arr.length || !targetId) {
      return ['Нет даты'];
    }

    for (const item of arr) {
      const newPath: any = [...path, { name: item.name, id: item.id, children: arr }];

      if (item.id === targetId) {
        return newPath;
      }

      if (item.children.length > 0) {
        const foundPath = findPathById(item.children, targetId, newPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }

    return null;
  };
  setLayoutSubheader(
    categoryIdActive || selectedCategoryId
      ? {
          // @ts-ignore

          breadcrumb: [
            {
              name: fm('PRODUCT.CATALOG.ALL.CATEGORIES'),
            },
            ...(findPathById(catalogCategories, categoryIdActive || selectedCategoryId) || []),
          ].map((element: any) => {
            return {
              title:
                element.name?.length > 30 ? `${element.name.slice(0, 30)} ... ` : element.name,
              hoverTitle: element.name,
              root: true,
              id: element.id,
              children: element.children,
              page:
                element.name === fm('PRODUCT.CATALOG.ALL.CATEGORIES')
                  ? 'products/catalog/categories'
                  : 'products/catalog',
              onClickCategory: (id: number) => {
                setSearchText('');
                setCategoryIdActive(id);
              },
              click: () => {
                setSearchText('');
                if (element.name === fm('PRODUCT.CATALOG.ALL.PRODUCTS')) {
                  categoriesRef.current?.resetCategories();
                } else {
                  setCategoryIdActive(element.id || 0);
                }
              },
              isActive: element.id === (categoryIdActive || selectedCategoryId),
            };
          }),
        }
      : {
          breadcrumb: [
            {
              title: fm('PRODUCT.CATALOG.ALL.CATEGORIES'),
              page: 'products/catalog/categories',
              root: true,
              translate: 'PRODUCT.CATALOG.ALL.CATEGORIES',
            },
          ],
        }
  );

  setLayoutFooter({ show: true });

  const resetProducts = useCallback(() => {
    fetch({
      page: 1,
      perPage: perPage || 10,
      filter,
      categoryId: 0,
      sort_type: selectedOption,
      locale,
    });
  }, [catalogCategories, filter]);

  if (loadingMe || loadingTypes) {
    return <Preloader />;
  }

  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message={intl.formatMessage({ id: 'PRODUCT.DELETE.TEXT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          clearDel();
          setAlertOpen(false);
        }}
        handleAgree={() => deleteAction()}
      />

      <div className={classes.container}>
        {/* <div className={classes.filterCol}> */}
        <FilterCategoriesTree
          categories={catalogCategories || []}
          resetProducts={resetProducts}
          searchProducts={fetch}
          filter={filter}
          loading={loading}
          perPage={perPage}
          setSearch={setSearch}
          selectedCategoryId={selectedCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
          isAdmin={me?.is_admin}
          hideFilters
          categoriesRef={categoriesRef}
        />
        <div className={classes.productsCol}>
          <div
            className={clsx(classes.card, {
              [classes.revers]: isSearching,
            })}
          >
            <div>
              <ProductCatalogHeader
                classes={classes}
                fetch={fetch}
                beforeFetch={() => {
                  window.scrollTo({ top: 0, behavior: 'auto' });
                  sessionStorage.setItem('productCatalogScrollPosition', '0');
                }}
                isAdmin={isAdmin}
                page={page}
                perPage={perPage}
                products={products}
                selectedCategoryId={selectedCategoryId}
                setViewType={setViewType}
                total={total}
                viewType={viewType}
              />
              {loading ? (
                <Preloader />
              ) : (
                <>
                  {!products || !products.length ? (
                    <Typography className={classes.empty} component='h5' variant='h5'>
                      {intl.formatMessage({ id: 'PRODUCT.CATALOG.LIST.EMPTY' })}
                    </Typography>
                  ) : (
                    <>
                      {effectiveViewType === 'table' ? (
                        <ProductsTableListComponent
                          data={products}
                          isAuthorized={isAuthorized}
                          me={me}
                        />
                      ) : (
                        <>
                          {effectiveViewType === 'list' ? (
                            <>
                              {isAdmin ? (
                                <ProductsTableListForAdminComponent
                                  data={products}
                                  isAuthorized={isAuthorized}
                                  isAdmin={!!isAdmin}
                                  me={me}
                                  addProductLoading={addProductLoading}
                                  handleCartDialog={handleCartDialog}
                                  editAction={editAction}
                                  handleDeleteDialog={handleDeleteDialog}
                                />
                              ) : (
                                <ProductsTableForUsersComponent
                                  data={products}
                                  isAuthorized={isAuthorized}
                                  me={me}
                                />
                              )}
                            </>
                          ) : (
                            <ProductsGrid me={me} data={products} />
                          )}
                        </>
                      )}
                      {fetch && (
                        <ProductCatalogHeader
                          classes={classes}
                          fetch={fetch}
                          beforeFetch={() => {
                            window.scrollTo({ top: 0, behavior: 'auto' });
                            sessionStorage.setItem('productCatalogScrollPosition', '0');
                          }}
                          isAdmin={isAdmin}
                          page={page}
                          perPage={perPage}
                          products={products}
                          selectedCategoryId={selectedCategoryId}
                          setViewType={setViewType}
                          total={total}
                          viewType={viewType}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    products: state.productsCatalog.products ?? [],
    loading: state.productsCatalog.loading,
    page: state.productsCatalog.page,
    perPage: state.productsCatalog.per_page,
    total: state.productsCatalog.total,
    deleteError: state.productsCatalog.delError,
    viewType: state.productsCatalog.viewType,
    isSearching: state.productsCatalog.isSearching,

    catalogCategories: state.categories.catalogCategories,

    me: state.profile.me,
    loadingMe: state.profile.loading,

    addProductLoading: state.cart.addProductLoading,
    addProductSuccess: state.cart.addProductSuccess,
    addProductError: state.cart.addProductError,
    cartCompanyId: state.cart.companyId,
    isAuthorized: state.auth.user != null,
    companyGuestCart: state.cart.guestCart?.cartCompanyId,
    isAdmin: state.auth.user?.is_admin,
    typeParams: state.productTypes.typeParams,
    productTypes: state.productTypes.productTypes,
    delSuccess: state.productsCatalog.delSuccess,
    loadingTypes: state.productTypes.loading,
    sortType: state.productsCatalog.sortType,
    search: state.productsCatalog.search,
    categoryIdActive: state.productsCatalog.categoryIdActive,
  }),
  {
    fetch: productCatalogActions.fetchRequest,
    clearProducts: productCatalogActions.clearProducts,
    delProduct: productCatalogActions.delRequest,
    clearDel: productCatalogActions.clearDel,
    setViewType: productCatalogActions.setViewType,
    setSearch: productCatalogActions.setSearch,
    setSearchText: productCatalogActions.setSearching,
    setCategoryIdActive: productCatalogActions.setCategoryIdActive,

    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,

    addProductToCart: cartActions.addProductRequest,
    setProductGuestCart: cartActions.setProductGuestCart,

    fetchCatalogCategories: categoriesActions.fetchCatalogCategories,

    fetchTypeById: productTypesActions.fetchParamsByIdRequest,
    clearParams: productTypesActions.clearParams,
    fetchType: productTypesActions.fetchRequest,

    clearAddProduct: cartActions.clearAddProduct,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ProductCatalog);
